import { useState } from 'react';
import {
    Star,
    Award,
    TrendingUp,
    Clock,
    Briefcase,
    UserPlus,
    Lock,
    Trophy,
    LineChart,
    CheckCircle,
    Target
} from 'lucide-react';
const Main = () => {
    const [showPremium, setShowPremium] = useState(false);

    // Mock top performer data
    const topPerformers = [
        {
            name: "Sarah Chen",
            role: "Senior IP Counsel",
            specialization: "AI/ML Patents",
            performance: {
                caseload: 85,
                successRate: 94,
                avgTurnaround: "15 days",
                clientSatisfaction: 96
            },
            expertise: ["Technical Patents", "Portfolio Strategy", "AI/ML"],
            recentWins: [
                {
                    client: "TechVision AI",
                    value: "£2.5M",
                    timeline: "3 weeks",
                    complexity: "High"
                }
            ],
            availability: "3 months notice",
            marketValue: "£180k-£220k"
        },
        {
            name: "James Wilson",
            role: "IP Strategy Director",
            specialization: "Portfolio Management",
            performance: {
                caseload: 72,
                successRate: 92,
                avgTurnaround: "12 days",
                clientSatisfaction: 98
            },
            expertise: ["IP Strategy", "Portfolio Optimization", "Tech Licensing"],
            recentWins: [
                {
                    client: "DataSphere Ltd",
                    value: "£1.8M",
                    timeline: "4 weeks",
                    complexity: "Medium"
                }
            ],
            availability: "Open to discussion",
            marketValue: "£160k-£200k"
        }
    ];
    return (
        <div className="space-y-6">
            {/* Premium Feature Lock */}
            {!showPremium && (
                <div className="p-[25px] rounded-[5px] shadow-2xl bg-gradient-to-r from-purple-50 to-blue-50">
                    <div>
                        <div className="flex items-center gap-2">
                            <Lock className="w-5 h-5" />
                            <span className='font-bold'>Premium Talent Insights</span>
                        </div>
                        <div>
                            Unlock access to top-performing legal professionals
                        </div>
                    </div>
                    <div>
                        <div className="grid grid-cols-3 gap-4 mb-6 mt-2">
                            <div className="p-4 bg-white rounded-lg shadow-sm">
                                <Trophy className="w-8 h-8 text-yellow-400 mb-2" />
                                <h3 className="font-medium">Top 5% Performers</h3>
                                <p className="text-sm text-gray-500">Access elite talent pool</p>
                            </div>
                            <div className="p-4 bg-white rounded-lg shadow-sm">
                                <LineChart className="w-8 h-8 text-blue-400 mb-2" />
                                <h3 className="font-medium">Performance Metrics</h3>
                                <p className="text-sm text-gray-500">Detailed success analytics</p>
                            </div>
                            <div className="p-4 bg-white rounded-lg shadow-sm">
                                <Target className="w-8 h-8 text-green-400 mb-2" />
                                <h3 className="font-medium">Direct Contact</h3>
                                <p className="text-sm text-gray-500">Engagement opportunities</p>
                            </div>
                        </div>
                        <button className="flex items-center gap-2 px-4 py-2 bg-green text-white rounded-lg w-full"

                            onClick={() => setShowPremium(true)}
                        >
                            Unlock Premium Access
                        </button>
                    </div>
                </div>
            )}

            {/* Top Performers List */}
            {showPremium && (
                <div className='bg-white p-[25px] rounded-[5px] shadow-2xl'>
                    <div>
                        <div className="flex items-center gap-2">
                            <Star className="w-5 h-5" />
                            <span className='font-bold'>Top Performing Professionals</span>
                        </div>
                        <div>
                            Top 5% of legal professionals by success rate and efficiency
                        </div>
                    </div>
                    <div>
                        <div className="space-y-6 mt-2">
                            {topPerformers.map((professional, index) => (
                                <div key={index} className="border rounded-lg p-4">
                                    {/* Header */}
                                    <div className="flex justify-between items-start mb-4">
                                        <div>
                                            <h3 className="font-medium">{professional.name}</h3>
                                            <p className="text-sm text-gray-500">
                                                {professional.role} • {professional.specialization}
                                            </p>
                                        </div>
                                        <div className="text-xs font-medium me-2 px-2.5 py-0.5 rounded-sm bg-[#d1fae5] text-[#065f46]">
                                            Top {index + 1}%
                                        </div>
                                    </div>

                                    {/* Performance Metrics */}
                                    <div className="grid grid-cols-4 gap-4 mb-4">
                                        <div>
                                            <div className="text-sm text-gray-500 mb-1">Caseload</div>
                                            <div className="font-medium">{professional.performance.caseload}</div>
                                        </div>
                                        <div>
                                            <div className="text-sm text-gray-500 mb-1">Success Rate</div>
                                            <div className="font-medium">{professional.performance.successRate}%</div>
                                        </div>
                                        <div>
                                            <div className="text-sm text-gray-500 mb-1">Turnaround</div>
                                            <div className="font-medium">{professional.performance.avgTurnaround}</div>
                                        </div>
                                        <div>
                                            <div className="text-sm text-gray-500 mb-1">Satisfaction</div>
                                            <div className="font-medium">{professional.performance.clientSatisfaction}%</div>
                                        </div>
                                    </div>

                                    {/* Expertise */}
                                    <div className="mb-4">
                                        <div className="text-sm text-gray-500 mb-2">Expertise</div>
                                        <div className="flex gap-2">
                                            {professional.expertise.map((exp, idx) => (
                                                <div key={idx} className="text-xs font-medium me-2 px-2.5 py-0.5 rounded-sm bg-blue-100 text-blue-800">
                                                    {exp}
                                                </div>
                                            ))}
                                        </div>
                                    </div>

                                    {/* Recent Success */}
                                    <div className="mb-4 p-3 bg-gray-50 rounded-lg">
                                        <div className="text-sm font-medium mb-2">Recent Success</div>
                                        {professional.recentWins.map((win, idx) => (
                                            <div key={idx} className="grid grid-cols-3 gap-2 text-sm">
                                                <div>
                                                    <span className="text-gray-500">Client:</span> {win.client}
                                                </div>
                                                <div>
                                                    <span className="text-gray-500">Value:</span> {win.value}
                                                </div>
                                                <div>
                                                    <span className="text-gray-500">Timeline:</span> {win.timeline}
                                                </div>
                                            </div>
                                        ))}
                                    </div>

                                    {/* Action Footer */}
                                    <div className="flex justify-between items-center pt-4 border-t">
                                        <div>
                                            <div className="text-sm text-gray-500">Market Value</div>
                                            <div className="font-medium">{professional.marketValue}</div>
                                        </div>
                                        <div className="flex gap-2">
                                            <button className="flex items-center gap-2 px-4 py-2 rounded-lg border border-green text-green">View Full Profile</button>
                                            <button className="flex items-center gap-2 px-4 py-2 bg-green text-white rounded-lg">
                                                <UserPlus className="w-4 h-4" />
                                                Initiate Contact
                                            </button>
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
            )}

            {/* Market Insights */}
            {showPremium && (
                <div className='bg-white p-[25px] rounded-[5px] shadow-2xl'>
                    <div>
                        <div className="flex items-center gap-2">
                            <TrendingUp className="w-5 h-5" />
                            <span className='font-bold'> Market Insights</span>
                        </div>
                    </div>
                    <div>
                        <div className="grid grid-cols-3 gap-4 mt-2">
                            <div className="p-4 border rounded-lg">
                                <h3 className="font-medium mb-2">Salary Trends</h3>
                                <div className="text-sm space-y-2">
                                    <div className="flex justify-between">
                                        <span className="text-gray-500">Senior IP Counsel</span>
                                        <span>+12% YoY</span>
                                    </div>
                                    <div className="flex justify-between">
                                        <span className="text-gray-500">IP Strategy Director</span>
                                        <span>+15% YoY</span>
                                    </div>
                                </div>
                            </div>
                            <div className="p-4 border rounded-lg">
                                <h3 className="font-medium mb-2">High Demand Areas</h3>
                                <div className="text-sm space-y-2">
                                    <div className="flex items-center gap-2">
                                        <div className="w-1 h-1 bg-blue-500 rounded-full"></div>
                                        AI/ML Patents (High)
                                    </div>
                                    <div className="flex items-center gap-2">
                                        <div className="w-1 h-1 bg-blue-500 rounded-full"></div>
                                        Portfolio Strategy (Medium)
                                    </div>
                                </div>
                            </div>
                            <div className="p-4 border rounded-lg">
                                <h3 className="font-medium mb-2">Availability</h3>
                                <div className="text-sm space-y-2">
                                    <div className="flex items-center gap-2">
                                        <div className="w-1 h-1 bg-green-500 rounded-full"></div>
                                        Immediate: 15%
                                    </div>
                                    <div className="flex items-center gap-2">
                                        <div className="w-1 h-1 bg-yellow-500 rounded-full"></div>
                                        3-6 months: 65%
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    )
}
export default Main